import { AdminUserRoles_ScopeLevel as AdminUserRolesScopeLevel } from '__generated__/graphql-types';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import type { GroupContextDefinition } from 'bundles/authoring/common/types/authoringCourseContexts';

import type { StaffManagementScopeData, StaffPermissionsLoadingEventNameType } from '../types/eventingTypes';

/**
 * Call the FE eventing client (i.e. Retracked) to record the page view event of
 * the staff management pages (on institution|program|course|group|session level)
 * @param {TrackingContextData} eventData contextual event data (e.g. information of the app and page, etc) required by Retracked (as part of the event key)
 * @param {StaffManagementScopeData} scopeData scope information (e.g. scope level, scope id, etc) for the page view event
 */
export const trackStaffManagementPageViewEvent = (
  eventData: TrackingContextData,
  scopeData: StaffManagementScopeData
): void => {
  Retracked.trackComponent(eventData, { ...scopeData }, 'staff_management_page', 'view');
};

/**
 * Call the FE eventing client (i.e. Retracked) to record the page view event of the staff permissions view
 * @param {TrackingContextData} eventData contextual event data (e.g. information of the app and page, etc) required by Retracked (as part of the event key)
 * @param {StaffManagementScopeData} ingressPointScopeData scope information (e.g. scope level, scope id, etc) of the ingress point
 * @param {number} targetUserId the user id of the target user
 */
export const trackStaffPermissionsPageViewEvent = (
  eventData: TrackingContextData,
  ingressPointScopeData: StaffManagementScopeData,
  targetUserId: number
): void => {
  Retracked.trackComponent(eventData, { targetUserId, ingressPointScopeData }, 'staff_permissions_view', 'view');
};

/**
 * Call the FE eventing client (i.e. Retracked) to record the loading time
 * @param {TrackingContextData} eventData contextual event data (e.g. information of the app and page, etc) required by Retracked (as part of the event key)
 * @param {number} loadingTime the time difference between the user profile is loaded and the staff permissions view is loaded
 * @param {number} targetUserId the user id of the target user
 * @param {StaffPermissionsLoadingEventNameType} loadingEventName the "component name" of the event key, used to specify the event name here
 * @param {boolean} emptyData whether the loaded data is empty or not, optional, default is false
 */
export const trackLoadingTime = (
  eventData: TrackingContextData,
  loadingTime: number,
  targetUserId: number,
  loadingEventName: StaffPermissionsLoadingEventNameType,
  emptyData = false
): void => {
  Retracked.trackComponent(eventData, { targetUserId, loadingTime, emptyData }, loadingEventName, 'view');
};

/**
 * Call the FE eventing client (i.e. Retracked) to record the time the user spent inside the staff permissions view
 * @param {TrackingContextData} eventData contextual event data (e.g. information of the app and page, etc) required by Retracked (as part of the event key)
 * @param {number} spentTime the time difference between the user exits the view and enters the view
 * @param {number} targetUserId the user id of the target user
 */
export const trackSpentTime = (eventData: TrackingContextData, spentTime: number, targetUserId: number): void => {
  Retracked.trackComponent(eventData, { targetUserId, spentTime }, 'staff_permissions_view', 'exit');
};

/**
 * Calculate the scope information for the course staff management page (could be either course-level or group-level)
 * @param {GroupContextDefinition | undefined} courseContext the course context/instance, its type could be GroupContextDefinition (the page is displayed in "Group Staff") or undefined (the page is displayed in "Course Staff")
 * @param {string} courseId the course id
 * @param {string} courseName the course name
 * @param {string} groupName the group name, for course-level staff management page, its value is passed as an empty string
 * @returns {StaffManagementScopeData} the computed scope data
 */
export const getCourseStaffManagementScopeData = (
  courseContext: GroupContextDefinition | undefined,
  courseId: string,
  courseName: string,
  groupName: string | undefined
): StaffManagementScopeData => {
  const isGroupStaffPage = !!courseContext?.groupId;
  return {
    scopeLevel: isGroupStaffPage ? AdminUserRolesScopeLevel.Group : AdminUserRolesScopeLevel.Course,
    scopeId: isGroupStaffPage ? (courseContext?.groupId as string) : courseId,
    scopeName: isGroupStaffPage ? groupName ?? '' : courseName,
    // course-level is the "top-level" and doesn't have a parent scope
    parentScopeId: isGroupStaffPage ? courseId : undefined,
    parentScopeName: isGroupStaffPage ? courseName : undefined,
  };
};
